import { baseApi } from '@/shared/api/baseApi';
import { setTokens } from '@/shared/lib/tokenStorage';

import {
  generalDataSchema,
  loginResponseSchema,
  requestOtpResponseSchema,
  shortUrlLoginSchema,
  verifyOtpResponseSchema,
  type GeneralData,
  type LinkPayload,
  type LoginResponse,
  type RequestOtpResponse,
  type ShortUrlLogin,
  type VerifyOtpResponse,
} from './auth.schema';

interface LoginArgs {
  payload: LinkPayload;
}

interface ShortUrlLoginArgs {
  shortUrlToken: string;
}

interface VerifyOtpArgs {
  asicMemberId: string;
  inputCode: string;
}

interface RequestOtpArgs {
  asicMemberId: string;
  forceResend?: boolean;
}

/**
 * Auth endpoints.
 *
 * `/director/login` is the one call that cannot use the shared middleware's
 * token: it authenticates with the single-use `authToken` from the emailed
 * link, which is what it trades for a session. Every other endpoint here relies
 * on the access token the middleware attaches.
 */
export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<LoginResponse, LoginArgs>({
      query: ({ payload }) => ({
        url: '/director/login',
        method: 'POST',
        body: {
          asicMemberId: payload.asicMemberId,
          documentsId: payload.documentsId,
          name1: payload.name1 ?? null,
          familyName: payload.familyName ?? null,
          hashCode: payload.hashCode ?? null,
        },
        headers: { authorization: payload.authToken },
      }),
      // The response is parsed, not cast: a login that returns a body without
      // an access token is a failed login, and Zod is what makes that a
      // rejected promise rather than `undefined` written into storage.
      transformResponse: (response: unknown, _meta, { payload }): LoginResponse => {
        const parsed = loginResponseSchema.parse(response);

        setTokens({
          accessToken: parsed.accessToken,
          refreshToken: parsed.refreshToken,
          userId: payload.asicMemberId,
          asicCompanyId: payload.asicCompanyId ?? undefined,
        });

        return parsed;
      },
      invalidatesTags: ['Session'],
    }),

    /**
     * Exchanges a short link (`/u/:token`) for a session.
     *
     * Unlike `/director/login`, the payload is not in the URL — the server
     * holds it and returns it alongside the token, so the destination can only
     * be computed after the exchange.
     */
    shortUrlLogin: build.mutation<ShortUrlLogin, ShortUrlLoginArgs>({
      query: (body) => ({
        url: '/director/short-url-login',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown): ShortUrlLogin => {
        const parsed = shortUrlLoginSchema.parse(response);

        setTokens({
          accessToken: parsed.accessToken,
          refreshToken: '',
          userId: parsed.decodedObject.asicMemberId,
          asicCompanyId: parsed.decodedObject.asicCompanyId ?? undefined,
        });

        return parsed;
      },
      invalidatesTags: ['Session'],
    }),

    verifyOtp: build.mutation<VerifyOtpResponse, VerifyOtpArgs>({
      query: (body) => ({ url: '/director/verifyOtp', method: 'PUT', body }),
      transformResponse: (response: unknown): VerifyOtpResponse =>
        verifyOtpResponseSchema.parse(response),
      invalidatesTags: ['Session'],
    }),

    requestOtp: build.mutation<RequestOtpResponse, RequestOtpArgs>({
      query: ({ asicMemberId, forceResend = false }) => ({
        url: '/director/requestOtp',
        method: 'PUT',
        body: { asicMemberId, forceResend },
      }),
      // Any 2xx is a send; an empty body just means the server had nothing to
      // add, and the screen falls back to its own copy.
      transformResponse: (response: unknown): RequestOtpResponse =>
        requestOtpResponseSchema.parse(response ?? {}),
      // The retry window is read from the body first and `Retry-After` last —
      // the legacy portal's precedence. The header is only reachable here, so it
      // travels on the error the screen reads.
      transformErrorResponse: (response, meta) => ({
        ...response,
        retryAfter: meta?.response?.headers.get('retry-after') ?? null,
      }),
    }),

    /** The director's own record. `/director/general` rather than a `/me`
     *  route: the session already identifies the ASIC member, and this is the
     *  endpoint that returns them. */
    getGeneralData: build.query<GeneralData, undefined>({
      query: () => '/director/general',
      transformResponse: (response: unknown): GeneralData => generalDataSchema.parse(response),
      providesTags: ['Session'],
    }),

    /** The Director Identification Number, which directors self-report. Lives
     *  with the session because it is a property of the signed-in director, not
     *  of any one company they belong to. */
    updateDirectorDin: build.mutation<undefined, { asicMemberId: string; din: string }>({
      query: ({ asicMemberId, din }) => ({
        url: `/director/${asicMemberId}/updateDin`,
        method: 'PUT',
        body: { din },
      }),
      invalidatesTags: ['Session', 'Company'],
    }),
  }),
});

export const {
  useLoginMutation,
  useShortUrlLoginMutation,
  useVerifyOtpMutation,
  useRequestOtpMutation,
  useGetGeneralDataQuery,
  useUpdateDirectorDinMutation,
} = authApi;
